import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Link, Tooltip, Typography } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { buildPreviewHtml, openHtmlInBrowser } from './htmlBoilerplate';
import { PROGRAMIZ } from './programizTheme';

/**
 * Live browser preview for HTML / CSS practice (client-side, no Piston).
 *
 * - Right-click inside the preview keeps the browser's default context menu
 *   (View Page Source, Inspect, Save As, …).
 * - "Open in Browser" (header) opens the same page in a new browser tab.
 */
export default function WebPreviewPanel({
  language = 'html',
  code = '',
  fillHeight = false,
  variant = 'programiz',
}) {
  const [manualUrl, setManualUrl] = useState('');

  const html = useMemo(
    () => buildPreviewHtml(language, code),
    [language, code],
  );
  const isProgramiz = variant === 'programiz';

  const handleOpenInBrowser = useCallback(() => {
    try {
      const { url } = openHtmlInBrowser(html);
      // Offer a direct link in case the browser still blocks the new tab.
      setManualUrl(url);
      window.setTimeout(() => setManualUrl(''), 15_000);
    } catch (err) {
      setManualUrl('');
      // eslint-disable-next-line no-alert
      window.alert(err?.message || 'Could not open in browser.');
    }
  }, [html]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: fillHeight ? '100%' : undefined,
        minHeight: fillHeight ? 0 : 220,
        bgcolor: isProgramiz ? PROGRAMIZ.bodyBg : '#1E1E1E',
        border: isProgramiz ? `1px solid ${PROGRAMIZ.border}` : '1px solid #3C3C3C',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          minHeight: 40,
          flexShrink: 0,
          bgcolor: isProgramiz ? PROGRAMIZ.headerBg : '#252526',
          borderBottom: isProgramiz
            ? `1px solid ${PROGRAMIZ.border}`
            : '1px solid #3C3C3C',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: 13,
            color: isProgramiz ? PROGRAMIZ.title : '#CCCCCC',
          }}
        >
          Preview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            variant="caption"
            sx={{ color: isProgramiz ? PROGRAMIZ.muted : '#858585', fontSize: 11 }}
          >
            Live
          </Typography>
          <Tooltip title="Open this page in a new browser tab">
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: '16px !important' }} />}
              onClick={handleOpenInBrowser}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                py: 0.25,
                px: 1,
                minHeight: 28,
                borderColor: isProgramiz ? PROGRAMIZ.border : '#3C3C3C',
                color: isProgramiz ? PROGRAMIZ.text : '#CCCCCC',
                '&:hover': {
                  borderColor: isProgramiz ? PROGRAMIZ.accent : '#007ACC',
                  bgcolor: isProgramiz ? 'rgba(37, 99, 235, 0.06)' : 'rgba(0,122,204,0.12)',
                },
              }}
            >
              Open in Browser
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {manualUrl ? (
        <Typography
          variant="caption"
          sx={{
            px: 1.5,
            py: 0.75,
            color: isProgramiz ? PROGRAMIZ.text : '#D4D4D4',
            bgcolor: isProgramiz ? '#EFF6FF' : '#1E3A5F',
            flexShrink: 0,
          }}
        >
          If a new tab did not open,{' '}
          <Link href={manualUrl} target="_blank" rel="noopener noreferrer" underline="hover">
            click here to open in browser
          </Link>
          .
        </Typography>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#FFFFFF' }}>
        <iframe
          title="Web design preview"
          sandbox="allow-scripts allow-modals allow-forms allow-popups allow-popups-to-escape-sandbox"
          srcDoc={html}
          style={{
            width: '100%',
            height: '100%',
            border: 0,
            display: 'block',
            background: '#fff',
          }}
        />
      </Box>
    </Box>
  );
}
